/**
 * Invite people (Figma Invite): addresses are added one at a time, each with the access it
 * will give (Member unless changed to Group leader — nobody is invited as a Facilitator), then
 * sent together.
 */
export type InviteChannel = 'email' | 'phone';
export type InviteRole = 'member' | 'leader';

export interface InviteDraft {
  id: string;
  channel: InviteChannel;
  /** The email, or the phone number in E.164. */
  address: string;
  role: InviteRole;
}

export interface PendingInvite {
  id: string;
  /** The invited person's actor (Core makes one for a new address); a resend names it. */
  inviteeId: string;
  address: string;
  role: InviteRole;
  /** ISO timestamp. */
  invitedAt: string;
  /** ISO timestamp. Core keeps a lapsed invitation pending, but it can no longer be accepted. */
  expiresAt: string;
}

/** Past its expiry: sending it again would send a link Core no longer accepts. */
export function inviteExpired(invite: Pick<PendingInvite, 'expiresAt'>, now = new Date()): boolean {
  return new Date(invite.expiresAt).getTime() <= now.getTime();
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Something that reads like an email address: text, an @, a domain with a dot. */
export function looksLikeEmail(value: string): boolean {
  return EMAIL.test(value.trim());
}

/** The same address however it was typed: emails ignore case and spaces. */
export function sameInviteAddress(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** Why an address can't join Ready to invite: it's on the list, or already invited to the group. */
export type InviteAddressProblem = 'ready' | 'pending';

export function inviteAddressProblem(
  address: string,
  ready: readonly InviteDraft[],
  pending: readonly PendingInvite[] = [],
): InviteAddressProblem | undefined {
  if (ready.some((invite) => sameInviteAddress(invite.address, address))) return 'ready';
  if (pending.some((invite) => sameInviteAddress(invite.address, address))) return 'pending';
  return undefined;
}

/** Whether `address` can join the list: not already on it, and not already invited. */
export function canAddInvite(
  address: string,
  ready: readonly InviteDraft[],
  pending: readonly PendingInvite[] = [],
): boolean {
  if (!address.trim()) return false;
  return inviteAddressProblem(address, ready, pending) === undefined;
}
